import React, {useEffect, useState} from 'react';
import Modal from 'react-modal';
import {uid} from 'uid';
import {ATTRIBUTES, SKILLS, localized} from '../../data/traits';
import {REQUEST_MODES, createRollRequest} from '../../rollRequest';
import {sendRollRequest} from '../../rollRequestRepository';
import {vtmT, currentLanguage} from '../../i18n';
import '../../../styles/vtm/roll.css';

/** Le MJ demande un jet à tout ou partie de la coterie : réserve (ou frénésie), difficulté, jet caché. */
const VtmRollRequestModal = ({isOpen, onClose, campaignUid, characters}) => {
  const lng = currentLanguage();
  const [mode, setMode] = useState(REQUEST_MODES.POOL);
  const [first, setFirst] = useState('');
  const [second, setSecond] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [hidden, setHidden] = useState(false);
  const [selected, setSelected] = useState([]);
  const [state, setState] = useState(null); // null | 'sending' | 'error'
  const isFrenzy = mode === REQUEST_MODES.FRENZY;

  // À l'ouverture : toute la coterie par défaut ; la réserve précédente reste, pour enchaîner les demandes
  useEffect(() => {
    if (!isOpen) return;
    setState(null);
    setSelected(characters.map(character => character.uid));
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (characterUid) => setSelected(selected.includes(characterUid) ? selected.filter(id => id !== characterUid) : [...selected, characterUid]);
  const allSelected = characters.length > 0 && selected.length === characters.length;

  const send = async (event) => {
    event.preventDefault();
    setState('sending');
    try {
      await sendRollRequest(campaignUid, createRollRequest({
        id: uid(),
        mode,
        traits: [first, second],
        difficulty: difficulty === '' ? null : Math.max(0, parseInt(difficulty, 10) || 0),
        hidden,
        targets: selected,
      }));
      onClose();
    } catch (e) {
      console.log(e);
      setState('error');
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} className="vtmModal" overlayClassName="vtmModalOverlay" contentLabel={vtmT('rollRequest')}>
      <div className="vtmModalHeader">
        <h2 className="vtmModalTitle">{vtmT('rollRequest')}</h2>
      </div>
      <p className="vtmModalHint">{vtmT('rollRequestHint')}</p>

      <form className="vtmRollForm" onSubmit={send}>
        <fieldset className="vtmRequestModes">
          <legend className="vtmFieldLabel">{vtmT('rollRequestKind')}</legend>
          <div className="vtmToggles">
            {[REQUEST_MODES.POOL, REQUEST_MODES.FRENZY].map(value => (
              <label key={value} className="vtmToggle" htmlFor={`vtmRequestMode${value}`}>
                <input id={`vtmRequestMode${value}`} type="radio" name="vtmRequestMode" checked={mode === value} onChange={() => setMode(value)} />
                <span>{value === REQUEST_MODES.FRENZY ? vtmT('frenzyCheck') : vtmT('rollRequestPool')}</span>
              </label>
            ))}
          </div>
        </fieldset>

        {!isFrenzy && (
          <div className="vtmFieldRow">
            <div className="vtmField">
              <label className="vtmFieldLabel" htmlFor="vtmRequestFirst">{vtmT('firstTrait')}</label>
              <select id="vtmRequestFirst" value={first} onChange={event => setFirst(event.target.value)}>
                <option value="">{vtmT('chooseTrait')}</option>
                {ATTRIBUTES.map(item => <option key={item.id} value={item.id}>{localized(item.name, lng)}</option>)}
              </select>
            </div>
            <span className="vtmPlus" aria-hidden="true">+</span>
            <div className="vtmField">
              <label className="vtmFieldLabel" htmlFor="vtmRequestSecond">{vtmT('rollRequestSecondTrait')}</label>
              <select id="vtmRequestSecond" value={second} onChange={event => setSecond(event.target.value)}>
                <option value="">{vtmT('none')}</option>
                <optgroup label={vtmT('skills')}>
                  {SKILLS.map(item => <option key={item.id} value={item.id}>{localized(item.name, lng)}</option>)}
                </optgroup>
                <optgroup label={vtmT('attributes')}>
                  {ATTRIBUTES.map(item => <option key={item.id} value={item.id}>{localized(item.name, lng)}</option>)}
                </optgroup>
              </select>
            </div>
          </div>
        )}

        <div className="vtmField">
          <label className="vtmFieldLabel" htmlFor="vtmRequestDifficulty">{vtmT('difficulty')}</label>
          <input id="vtmRequestDifficulty" type="number" min="0" inputMode="numeric" placeholder={vtmT('difficultyUnknown')} value={difficulty} onChange={event => setDifficulty(event.target.value)} />
        </div>

        <div className="vtmToggles">
          <label className="vtmToggle" htmlFor="vtmRequestHidden">
            <input id="vtmRequestHidden" type="checkbox" checked={hidden} onChange={event => setHidden(event.target.checked)} />
            <span>{vtmT('hiddenRoll')}</span>
          </label>
        </div>

        <fieldset className="vtmSceneRecipients">
          <legend className="vtmFieldLabel">{vtmT('rollRequestTargets')}</legend>
          {characters.length === 0 ? (
            <p className="vtmMutedInline">{vtmT('noCharacters')}</p>
          ) : (
            <div className="vtmToggles">
              <label className="vtmToggle" htmlFor="vtmRequestAll">
                <input id="vtmRequestAll" type="checkbox" checked={allSelected} onChange={() => setSelected(allSelected ? [] : characters.map(character => character.uid))} />
                <span>{vtmT('sceneEveryone')}</span>
              </label>
              {characters.map(character => (
                <label key={character.uid} className="vtmToggle vtmSceneMember" htmlFor={`vtmRequest${character.uid}`}>
                  <input id={`vtmRequest${character.uid}`} type="checkbox" checked={selected.includes(character.uid)} onChange={() => toggle(character.uid)} />
                  <span>{character.name || vtmT('newCharacter')}</span>
                </label>
              ))}
            </div>
          )}
        </fieldset>

        {state === 'error' && <p className="vtmXpError" role="alert">{vtmT('rollRequestError')}</p>}

        <div className="vtmModalActions">
          <button type="submit" className="vtmButton primary" disabled={(!isFrenzy && !first) || selected.length === 0 || state === 'sending'}>
            {vtmT('rollRequestSend')}
          </button>
          <button type="button" className="vtmButton ghost" onClick={onClose}>{vtmT('close')}</button>
        </div>
      </form>
    </Modal>
  );
};

export default VtmRollRequestModal;
